import type { DaySlotAvailability, GetAvailableSlotsOutput, SlotPaymentMethod } from '@api/models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { MemberSelectionResult } from '@ui/components/features/selection/memberSelector';
import { getAvailableSlots } from '@website/services/bookings.service';
import type { TimeSlotSelection } from '@website/types/slot';
import { instantLocalDay, slotInstantMs, slotLocalDay } from '@website/utils/timezone';

import { BookingFormData } from '../app/[locale]/activity/[id]/slots/lib';
import { DateItem } from '../utils/date';

/**
 * Availability entry tagged with the occurrence's UTC slot_date. The backend
 * groups occurrences by slot_date (a UTC calendar date); we re-bucket them by
 * the VIEWER's local day (see {@link regroupAvailabilityByLocalDate}) so the
 * date pills line up with the local times shown - but each entry keeps its
 * `utcDate` because that, not the local pill date, is the booking's server
 * identity (`bookingDate`, matched against `slot_date`).
 */
interface LocalSlotAvailability {
  slotId: string;
  utcDate: string;
  timeSlots: DaySlotAvailability['timeSlots'];
}

type LocalAvailability = Record<string, Record<string, LocalSlotAvailability[]>>;

/**
 * Re-bucket the server availability (keyed by UTC slot_date → month/day) into a
 * structure keyed by the viewer's LOCAL month/day, derived from each
 * occurrence's true instant. Each occurrence keeps its UTC slot_date so the
 * exact occurrence can still be booked. A single occurrence is placed under the
 * local day its start time falls on for the current viewer.
 */
export function regroupAvailabilityByLocalDate(raw: GetAvailableSlotsOutput): LocalAvailability {
  const out: LocalAvailability = {};
  for (const monthKey of Object.keys(raw)) {
    const days = raw[monthKey];
    for (const dayKey of Object.keys(days)) {
      const utcDate = `${monthKey}-${String(parseInt(dayKey, 10)).padStart(2, '0')}`;
      for (const entry of days[dayKey]) {
        for (const ts of entry.timeSlots) {
          // Bucket by the browser-zone day of the occurrence's absolute instant
          // (server-resolved `startAt`); fall back to the venue-resolved instant
          // for payloads that predate startAt.
          const local = ts.startAt !== undefined ? instantLocalDay(ts.startAt) : slotLocalDay(utcDate, ts.startTime);
          const localMonth = `${local.getFullYear()}-${(local.getMonth() + 1).toString().padStart(2, '0')}`;
          const localDay = String(local.getDate());
          const month = (out[localMonth] ??= {});
          const dayArr = (month[localDay] ??= []);
          let bucket = dayArr.find((e) => e.slotId === entry.slotId && e.utcDate === utcDate);
          if (!bucket) {
            bucket = { slotId: entry.slotId, utcDate, timeSlots: [] };
            dayArr.push(bucket);
          }
          bucket.timeSlots.push(ts);
        }
      }
    }
  }
  return out;
}

// Helper to set payment method form values based on slot's accepted methods
function setPaymentMethodFormValues(
  form: UseFormReturn<BookingFormData>,
  paymentMethods: SlotPaymentMethod[] | undefined,
  isFreeBooking: boolean = false
) {
  if (!paymentMethods || paymentMethods.length === 0) {
    // Default to online if no payment methods specified (shouldn't happen)
    form.setValue('selectedSlotPaymentMethods', ['online']);
    form.setValue('paymentMethod', 'online');
    return;
  }

  form.setValue('selectedSlotPaymentMethods', paymentMethods);

  // Free bookings never touch Stripe; auto-select a payment method so the user
  // isn't asked to choose one. The server transitions these to `FREE` via
  // confirmBooking regardless of which value we send.
  if (isFreeBooking) {
    const cashMethod = paymentMethods.find((m) => m === 'cash');
    const preferred = cashMethod ?? paymentMethods[0];
    form.setValue('paymentMethod', preferred);
    return;
  }

  // Auto-select if only one payment method is available
  if (paymentMethods.length === 1) {
    form.setValue('paymentMethod', paymentMethods[0]);
  } else {
    // Clear payment method selection when multiple options - user will choose
    form.setValue('paymentMethod', undefined);
  }
}

// Timezone-safe date formatting function
function formatDateToLocalString(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Helper to capitalize first letter (for languages like French where months/days are lowercase)
const capitalizeFirst = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

interface SelectedSlot {
  displayTime: string;
  slotId: string;
  slot: {
    startTime: number;
    endTime: number;
    utcDate: string;
    slotId: string;
    type: 'SINGLE' | 'GROUP';
    maxPeople?: number;
  };
  price: number;
}

export type BookingTypeFilter = 'all' | 'SINGLE' | 'GROUP' | 'RANGE';

interface UsePickSlotLogicParams {
  slots: any[];
  form: UseFormReturn<BookingFormData>;
  locale?: string;
  bookingTypeLabels?: {
    SINGLE: string;
    GROUP: string;
    RANGE?: string;
  };
}

// Enriched availability entry with slot name for display
export interface EnrichedSlotAvailability extends LocalSlotAvailability {
  slotName?: string;
}

/**
 * Compound hook that handles all slot picking logic including:
 * - Fetching 6 months of availability data in a single call for ALL slots
 * - Month selection from available months
 * - Date availability calculation for selected month
 * - Slot processing and time slot generation
 * - Client-side slot type filtering (all / SINGLE / GROUP)
 * - Slot selection and member selection for group slots
 */
export function usePickSlotLogic({ slots, form, locale = 'en', bookingTypeLabels }: UsePickSlotLogicParams) {
  const formData = form.watch();

  // State for availability data from API (all 6 months), re-bucketed into the
  // viewer's local month/day (see regroupAvailabilityByLocalDate).
  const [availabilityData, setAvailabilityData] = useState<LocalAvailability | null>(null);
  // State to track the last fetched slot IDs to prevent redundant calls
  const [lastFetchedSlotIds, setLastFetchedSlotIds] = useState<string[]>([]);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);

  // Client-side filter for slot type
  const [bookingTypeFilter, setBookingTypeFilter] = useState<BookingTypeFilter>('all');

  // Selected month state (YYYY-MM format, viewer-local)
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  // The local day pill the user is viewing. Distinct from formData.bookingDate,
  // which carries the chosen occurrence's UTC slot_date for submission.
  const [selectedLocalDay, setSelectedLocalDay] = useState<Date | null>(null);

  // Validate if slots are ready and properly loaded
  const isSlotsReady = useMemo(() => {
    return slots && Array.isArray(slots) && slots.length > 0 && slots.some((slot) => slot?.id && slot?.type);
  }, [slots]);

  // The distinct slot kinds the activity offers (controls filter visibility:
  // the filter only shows when there are at least two kinds to choose between).
  const availableBookingTypes = useMemo((): Array<'SINGLE' | 'GROUP' | 'RANGE'> => {
    if (!isSlotsReady) return [];
    return (['SINGLE', 'GROUP', 'RANGE'] as const).filter((kind) => slots.some((s) => s?.type === kind));
  }, [slots, isSlotsReady]);
  const hasBothBookingTypes = availableBookingTypes.length >= 2;

  // Build a lookup map from slotId → slot for enrichment
  const slotMap = useMemo(() => {
    const map: Record<string, any> = {};
    slots.forEach((s) => {
      if (s?.id) map[s.id] = s;
    });
    return map;
  }, [slots]);

  // Extract ALL slot IDs for the API call (no type filtering - server handles availability)
  const slotIds = useMemo(() => {
    if (!isSlotsReady) return [];
    return slots.filter((slot) => slot?.id).map((slot) => slot.id);
  }, [slots, isSlotsReady]);

  // Fetch availability data when slots change (single call for 6 months, all slot types)
  useEffect(() => {
    if (slotIds.length === 0) {
      setAvailabilityData(null);
      setSelectedMonth(null);
      return;
    }

    // Check if data for the slotIds has already been fetched
    const isSameSlotIds = JSON.stringify(lastFetchedSlotIds) === JSON.stringify(slotIds);

    if (isSameSlotIds && availabilityData !== null) {
      return; // No need to refetch
    }

    const fetchAvailability = async () => {
      setIsLoadingAvailability(true);
      try {
        const result = await getAvailableSlots({ slotIds: slotIds });
        const localized = regroupAvailabilityByLocalDate(result);
        setAvailabilityData(localized);
        setLastFetchedSlotIds(slotIds);

        // Auto-select the month to open. Honor a deep-linked / restored date
        // (bookingDate comes from the URL `date` param): if it falls in one of
        // the available months, open THAT month so the rail lands on the
        // requested day. Without this, a future-month deep-link (e.g. ?date in
        // July while today is June) snapped to the first available month and the
        // selected pill defaulted to the first available day. Falls back to the
        // first available month when there is no usable deep-linked date.
        const months = Object.keys(localized).sort();
        if (months.length > 0) {
          const deepLinkedMonth = form.getValues('bookingDate')?.slice(0, 7) || null;
          setSelectedMonth(deepLinkedMonth && months.includes(deepLinkedMonth) ? deepLinkedMonth : months[0]);
        } else {
          setSelectedMonth(null);
        }
      } catch (error) {
        console.error('Failed to fetch availability:', error);
        setAvailabilityData(null);
        setSelectedMonth(null);
      } finally {
        setIsLoadingAvailability(false);
      }
    };

    fetchAvailability();
  }, [slotIds, lastFetchedSlotIds, availabilityData, form]);

  useEffect(() => {
    setIsLoadingTimeSlots(true);
    if (availabilityData) {
      setIsLoadingTimeSlots(false);
    }
  }, [availabilityData]);

  // Derive available months from API data (sorted)
  const availableMonths = useMemo(() => {
    if (!availabilityData) return [];
    return Object.keys(availabilityData).sort();
  }, [availabilityData]);

  // Get month data for the selected month
  const selectedMonthData = useMemo(() => {
    if (!availabilityData || !selectedMonth) return null;
    return availabilityData[selectedMonth] || null;
  }, [availabilityData, selectedMonth]);

  // Filtered, bookable DateItems for one month's data. Empty days (no bookable,
  // not-yet-started slot matching the active filter) are excluded - so a date pill
  // never opens an empty "no slots" panel. Reused for the selected month and for
  // the full cross-month rail.
  const datesForMonth = useCallback(
    (monthKey: string | null, monthData: Record<string, LocalSlotAvailability[]> | null): DateItem[] => {
      if (!monthData || !monthKey) return [];
      const [yearStr, monthStr] = monthKey.split('-');
      const year = parseInt(yearStr);
      const month = parseInt(monthStr) - 1; // JS months are 0-indexed

      const now = new Date();
      const nowMs = now.getTime();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      return Object.keys(monthData)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map((dayKey) => {
          const day = parseInt(dayKey);
          const date = new Date(year, month, day);
          return {
            date,
            dayName: capitalizeFirst(date.toLocaleDateString(locale, { weekday: 'short' })),
            dayNumber: day
          };
        })
        .filter((item) => {
          if (item.date < today) return false;
          const dayData = monthData[item.dayNumber.toString()] || [];
          return dayData.some((entry) => {
            if (bookingTypeFilter !== 'all' && slotMap[entry.slotId]?.type !== bookingTypeFilter) {
              return false;
            }
            return entry.timeSlots.some((ts) => {
              if (ts.kind === 'RANGE') {
                const minimal = ts.fixedDurations?.length ? Math.min(...ts.fixedDurations) : 30;
                const endAt = ts.endAt ?? slotInstantMs(entry.utcDate, ts.endTime);
                return endAt - minimal * 60_000 >= nowMs;
              }
              return ts.startAt !== undefined
                ? ts.startAt >= nowMs
                : slotInstantMs(entry.utcDate, ts.startTime) >= nowMs;
            });
          });
        });
    },
    [locale, bookingTypeFilter, slotMap]
  );

  const availableDates = useMemo(
    (): DateItem[] => datesForMonth(selectedMonth, selectedMonthData),
    [datesForMonth, selectedMonth, selectedMonthData]
  );

  // Every bookable day across ALL months, one sorted list - feeds the single-row
  // date rail so the buyer scrolls straight across months (no separate month nav).
  const allAvailableDates = useMemo((): DateItem[] => {
    if (!availabilityData) return [];
    return availableMonths
      .flatMap((mk) => datesForMonth(mk, availabilityData[mk]))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [availabilityData, availableMonths, datesForMonth]);

  // All dates in availableDates have availability (since we only include days from the API response)
  const dateAvailability = useMemo(() => {
    const map: Record<string, boolean> = {};
    availableDates.forEach((item) => {
      map[item.date.toDateString()] = true;
    });
    return map;
  }, [availableDates]);

  // Calculate the selected LOCAL day pill (use existing selection or first available).
  const selectedTimeStart = formData.timeSlot?.startTime;
  const selectedDate = useMemo(() => {
    const isUsable = (d: Date | null): d is Date => {
      if (!d || !selectedMonth) return false;
      const monthKey = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
      return monthKey === selectedMonth && !!dateAvailability[d.toDateString()];
    };

    // 1. The pill the user explicitly selected.
    if (isUsable(selectedLocalDay)) return selectedLocalDay;

    // 2. The local day of the chosen occurrence. bookingDate is its UTC
    //    slot_date, so resolve the real local day from (slot_date + start).
    if (formData.bookingDate && selectedTimeStart) {
      const occurrenceDay = slotLocalDay(formData.bookingDate, selectedTimeStart);
      if (isUsable(occurrenceDay)) return occurrenceDay;
    }

    // 3. Deep-linked date with no time chosen yet - treat bookingDate as a local
    //    day hint (correct whenever it doesn't cross the UTC day boundary).
    if (formData.bookingDate) {
      const [y, m, d] = formData.bookingDate.split('-').map(Number);
      const hinted = new Date(y, m - 1, d);
      if (isUsable(hinted)) return hinted;
    }

    // 4. First available local day.
    return availableDates[0]?.date ?? null;
  }, [selectedLocalDay, formData.bookingDate, selectedTimeStart, selectedMonth, availableDates, dateAvailability]);

  // Get raw availability data for the selected date, enriched with slot names
  // and filtered by bookingTypeFilter
  const dayAvailabilityData = useMemo((): EnrichedSlotAvailability[] => {
    if (!selectedDate || !selectedMonthData) return [];

    const dayKey = selectedDate.getDate().toString();
    const raw = selectedMonthData[dayKey] || [];

    return raw
      .filter((entry) => {
        if (bookingTypeFilter === 'all') return true;
        return slotMap[entry.slotId]?.type === bookingTypeFilter;
      })
      .map((entry) => {
        const slot = slotMap[entry.slotId];
        const translatedBookingType =
          slot?.type && bookingTypeLabels ? bookingTypeLabels[slot.type as 'SINGLE' | 'GROUP' | 'RANGE'] : undefined;

        // For group slots, append the max number of participants
        let slotName = translatedBookingType ?? slot?.name;
        if (slot?.type === 'GROUP' && slot?.maxPeople && slotName) {
          slotName = `${slotName} (${slot.maxPeople})`;
        }

        return {
          ...entry,
          // Slot cards display the slot type label, not the custom slot title.
          slotName
        };
      });
  }, [selectedDate, selectedMonthData, bookingTypeFilter, slotMap, bookingTypeLabels]);

  // Split the day's entries: open-range entries feed the range model,
  // everything else feeds the fixed-chip grid (which must never see RANGE
  // entries - convertApiSlotsToDisplaySlots would render each free window as a
  // bookable chip at the HOURLY price).
  const dayChipEntries = useMemo(
    () =>
      dayAvailabilityData.filter(
        (entry) => slotMap[entry.slotId]?.type !== 'RANGE' && !entry.timeSlots.some((ts) => ts.kind === 'RANGE')
      ),
    [dayAvailabilityData, slotMap]
  );
  const dayRangeEntries = useMemo(
    () =>
      dayAvailabilityData.filter(
        (entry) => slotMap[entry.slotId]?.type === 'RANGE' || entry.timeSlots.some((ts) => ts.kind === 'RANGE')
      ),
    [dayAvailabilityData, slotMap]
  );

  // Find selected time slot ID for UI component based on UTC times
  const selectedTimeSlotId = useMemo(() => {
    if (!formData.timeSlot || formData.timeSlot.startTime === 0) return undefined;
    // A range pick lives in the range cards, never the chip grid - matching by
    // times alone could highlight an unrelated chip that shares the window.
    if (formData.bookingType === 'RANGE') return undefined;

    // Generate the same ID format that will be used by the wrapper component
    let foundSlotId = '';
    dayChipEntries.forEach((slotAvailability) => {
      slotAvailability.timeSlots.forEach((apiSlot) => {
        if (apiSlot.startTime === formData.timeSlot.startTime && apiSlot.endTime === formData.timeSlot.endTime) {
          foundSlotId = slotAvailability.slotId;
        }
      });
    });

    return foundSlotId ? `${foundSlotId}-slot-${formData.timeSlot.startTime}-${formData.timeSlot.endTime}` : undefined;
  }, [dayChipEntries, formData.timeSlot, formData.bookingType]);

  // Current range pick (for the unified picker's highlight/rehydration).
  const selectedRangeValue = useMemo(() => {
    if (formData.bookingType !== 'RANGE') return null;
    if (!formData.slotId || !formData.bookingDate || !formData.timeSlot || formData.timeSlot.endTime === 0) return null;
    return {
      slotId: formData.slotId,
      utcDate: formData.bookingDate,
      startTime: formData.timeSlot.startTime,
      endTime: formData.timeSlot.endTime
    };
  }, [formData.bookingType, formData.slotId, formData.bookingDate, formData.timeSlot]);

  // Member selection modal state
  const [showMemberSelection, setShowMemberSelection] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot | null>(null);

  // Handle month change
  const handleMonthChange = useCallback(
    (monthKey: string) => {
      setSelectedMonth(monthKey);
      // Clear current date selection when changing months
      setSelectedLocalDay(null);
      form.setValue('bookingDate', '');
      form.setValue('timeSlot', { startTime: 0, endTime: 0 });
    },
    [form]
  );

  // Handle date change. newDate is a local day pill. bookingDate is set
  // optimistically to the local string (correct for non-boundary occurrences)
  // and is overwritten with the exact UTC slot_date once a time is selected.
  const handleDateChange = useCallback(
    (newDate: Date) => {
      setSelectedLocalDay(newDate);
      // Keep selectedMonth in sync so picking a day in another month resolves
      // (selectedDate / day data are keyed by selectedMonth).
      setSelectedMonth(`${newDate.getFullYear()}-${(newDate.getMonth() + 1).toString().padStart(2, '0')}`);
      form.setValue('bookingDate', formatDateToLocalString(newDate));
      form.setValue('timeSlot', { startTime: 0, endTime: 0 });
    },
    [form]
  );

  // Handle time slot selection with clean UTC data from wrapper
  const handleTimeSlotSelect = useCallback(
    (selection: TimeSlotSelection) => {
      const { utcStartTime, utcEndTime, utcDate, slotId, price, displayTime } = selection;

      const timeSlotObject = {
        startTime: utcStartTime,
        endTime: utcEndTime
      };

      // Check if deselecting the same slot. A RANGE pick never matches a chip -
      // it can share the exact window with one, and tapping that chip must
      // SELECT the chip, not deselect the range pick.
      const isCurrentlySelected =
        formData.bookingType !== 'RANGE' &&
        formData.timeSlot &&
        formData.timeSlot.startTime === utcStartTime &&
        formData.timeSlot.endTime === utcEndTime &&
        // Two distinct slots can share a window - only the exact same slot deselects;
        // tapping a different one switches to it.
        formData.slotId === slotId &&
        // A both-mode slot has two same-window rows; only the matching mode deselects.
        (formData.locationId ?? undefined) === (selection.locationId ?? undefined);

      if (isCurrentlySelected) {
        form.setValue('timeSlot', { startTime: 0, endTime: 0 });
        form.setValue('slotId', '');
        form.setValue('basePrice', 0);
        form.setValue('locationId', undefined);
        form.setValue('locationKind', undefined);
        form.setValue('slotLocation', undefined);
        form.setValue('selectedSlotPaymentMethods', undefined);
        form.setValue('paymentMethod', undefined);
      } else {
        // Find the slot to get payment methods, type, and other data
        const slotData = slotMap[slotId];
        const isGroupSlot = slotData?.type === 'GROUP';

        // Sync bookingType form field from the selected slot's slot
        if (slotData?.type) {
          form.setValue('bookingType', slotData.type);
        }

        // Derive payment methods. Slot-level overrides (from persisted slot table) take priority
        // over slot-level defaults when present in the availability response.
        const effectiveAcceptsCash = selection.acceptsCash ?? slotData?.acceptsCash;
        const effectiveAcceptsOnline = selection.acceptsOnline ?? slotData?.acceptsOnline;
        const resolvedPaymentMethods: SlotPaymentMethod[] = slotData?.paymentMethods ?? [
          ...(effectiveAcceptsCash ? (['cash'] as const) : []),
          ...(effectiveAcceptsOnline ? (['online'] as const) : [])
        ];

        const isFreeBooking = price <= 0;

        // Freeze the chosen delivery location (physical/online) for the booking.
        form.setValue('locationId', selection.locationId);
        form.setValue('locationKind', selection.locationKind);
        // Freeze the per-slot resolved location so the summary/review show THIS
        // slot's address and the radius check uses its coords (legacy slots send
        // no location → undefined, and the wizard falls back to the activity).
        form.setValue('slotLocation', selection.location);

        // Pin the viewed pill to the occurrence's local day so it stays
        // consistent even when selectedDate was resolved via a fallback.
        setSelectedLocalDay(slotLocalDay(utcDate, utcStartTime));

        // For GROUP slots, show member selection modal
        if (isGroupSlot) {
          setSelectedSlot({
            displayTime,
            slotId: `${slotId}-slot-${utcStartTime}-${utcEndTime}`,
            slot: {
              startTime: utcStartTime,
              endTime: utcEndTime,
              utcDate,
              slotId: slotId,
              type: slotData?.type || 'GROUP',
              maxPeople: slotData?.maxPeople
            },
            price: price
          });
          setShowMemberSelection(true);

          setPaymentMethodFormValues(form, resolvedPaymentMethods, isFreeBooking);
        } else {
          // For SINGLE slots, directly set form values. bookingDate is the
          // occurrence's UTC slot_date - the server's occurrence identity.
          form.setValue('bookingDate', utcDate);
          form.setValue('timeSlot', timeSlotObject);
          form.setValue('slotId', slotId);
          form.setValue('basePrice', price);

          setPaymentMethodFormValues(form, resolvedPaymentMethods, isFreeBooking);
        }
      }
    },
    [formData.timeSlot, formData.bookingType, formData.locationId, formData.slotId, slotMap, form]
  );

  // Handle an open-range pick from the unified picker. A completed pick
  // (start + duration) commits directly - mirroring the SINGLE chip path -
  // and `null` clears it (deselect).
  const handleRangeSelect = useCallback(
    (selection: TimeSlotSelection | null) => {
      if (!selection) {
        form.setValue('timeSlot', { startTime: 0, endTime: 0 });
        form.setValue('slotId', '');
        form.setValue('basePrice', 0);
        form.setValue('locationId', undefined);
        form.setValue('locationKind', undefined);
        form.setValue('slotLocation', undefined);
        form.setValue('selectedSlotPaymentMethods', undefined);
        form.setValue('paymentMethod', undefined);
        return;
      }
      const { utcStartTime, utcEndTime, utcDate, slotId, price } = selection;
      const slotData = slotMap[slotId];

      const effectiveAcceptsCash = selection.acceptsCash ?? slotData?.acceptsCash;
      const effectiveAcceptsOnline = selection.acceptsOnline ?? slotData?.acceptsOnline;
      const resolvedPaymentMethods: SlotPaymentMethod[] = slotData?.paymentMethods ?? [
        ...(effectiveAcceptsCash ? (['cash'] as const) : []),
        ...(effectiveAcceptsOnline ? (['online'] as const) : [])
      ];

      setSelectedLocalDay(slotLocalDay(utcDate, utcStartTime));
      form.setValue('bookingDate', utcDate);
      form.setValue('timeSlot', { startTime: utcStartTime, endTime: utcEndTime });
      form.setValue('slotId', slotId);
      form.setValue('bookingType', 'RANGE');
      form.setValue('numberOfPeople', 1);
      form.setValue('basePrice', price);
      form.setValue('locationId', selection.locationId);
      form.setValue('locationKind', selection.locationKind);
      form.setValue('slotLocation', selection.location);
      setPaymentMethodFormValues(form, resolvedPaymentMethods, price <= 0);
    },
    [form, slotMap]
  );

  // Handle member selection confirmation
  const handleMemberConfirm = useCallback(
    (result: MemberSelectionResult) => {
      if (!selectedSlot) return;

      // For member selection, use the UTC slot_date + times stored in selectedSlot.slot
      if (selectedSlot?.slot) {
        form.setValue('bookingDate', selectedSlot.slot.utcDate || '');
        form.setValue('timeSlot', {
          startTime: selectedSlot.slot.startTime || 0,
          endTime: selectedSlot.slot.endTime || 0
        });
        form.setValue('slotId', selectedSlot.slot.slotId || '');
        form.setValue('bookingType', selectedSlot.slot.type);
      }
      form.setValue('numberOfPeople', result.memberCount);
      form.setValue('basePrice', result.totalPrice);

      setShowMemberSelection(false);
      setSelectedSlot(null);
    },
    [selectedSlot, form]
  );

  // Handle member selection modal close
  const handleMemberClose = useCallback(() => {
    setShowMemberSelection(false);
    setSelectedSlot(null);
  }, []);

  return {
    // Month data
    availableMonths,
    selectedMonth,
    handleMonthChange,

    // Date data
    availableDates,
    allAvailableDates,
    dateAvailability,
    selectedDate,
    dayAvailabilityData,
    dayChipEntries,
    dayRangeEntries,
    selectedTimeSlotId,
    selectedRangeValue,

    // Slot type filter
    bookingTypeFilter,
    setBookingTypeFilter,
    hasBothBookingTypes,
    availableBookingTypes,

    // Member selection state
    showMemberSelection,
    selectedSlot,

    // Handlers
    handleDateChange,
    handleTimeSlotSelect,
    handleRangeSelect,
    handleMemberConfirm,
    handleMemberClose,

    // Loading states
    isLoadingAvailability,
    isLoadingTimeSlots
  };
}
